import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SoundProvider } from "./lib/sound";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import { GoogleAuthGate } from "./components/AuthCallback";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import LevelSelect from "./pages/LevelSelect";
import Play from "./pages/Play";
import Daily from "./pages/Daily";
import Endless from "./pages/Endless";
import Friends from "./pages/Friends";
import Challenge from "./pages/Challenge";
import Skins from "./pages/Skins";
import Artifacts from "./pages/Artifacts";
import Season from "./pages/Season";
import Tournaments from "./pages/Tournaments";
import TournamentDetail from "./pages/TournamentDetail";
import Replay from "./pages/Replay";
import EndlessReplay from "./pages/EndlessReplay";
import Sector from "./pages/Sector";
import Achievements from "./pages/Achievements";
import Builder from "./pages/Builder";
import Community from "./pages/Community";
import CommunityLevel from "./pages/CommunityLevel";
import Creator from "./pages/Creator";
import Admin from "./pages/Admin";
import BotDetail from "./pages/BotDetail";
import Wanted from "./pages/Wanted";
import Unlock from "./pages/Unlock";
import UnlockSuccess from "./pages/UnlockSuccess";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Founders from "./pages/Founders";
import PaywallGuard from "./components/PaywallGuard";
import IosInstallHint from "./components/IosInstallHint";
import GlobalErrorHandler from "./components/GlobalErrorHandler";
import LiveNotificationToast from "./components/LiveNotificationToast";
import TrialBanner from "./components/TrialBanner";
import useAttributionTracker from "./lib/useAttributionTracker";
import { Toaster } from "sonner";

export default function App() {
  useAttributionTracker();
  return (
    <div className="App">
      <AppErrorBoundary>
        <SoundProvider>
          <AuthProvider>
            <BrowserRouter>
            <TrialBanner />
            <GoogleAuthGate>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/levels"
                element={
                  <ProtectedRoute>
                    <PaywallGuard>
                      <LevelSelect />
                    </PaywallGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/play/:level"
                element={
                  <ProtectedRoute>
                    <PaywallGuard>
                      <Play />
                    </PaywallGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/daily"
                element={
                  <ProtectedRoute>
                    <PaywallGuard>
                      <Daily />
                    </PaywallGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/endless"
                element={
                  <ProtectedRoute>
                    <PaywallGuard>
                      <Endless />
                    </PaywallGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/friends"
                element={
                  <ProtectedRoute>
                    <Friends />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/challenge/:token"
                element={
                  <ProtectedRoute>
                    <Challenge />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skins"
                element={
                  <ProtectedRoute>
                    <Skins />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/artifacts"
                element={
                  <ProtectedRoute>
                    <Artifacts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/season"
                element={
                  <ProtectedRoute>
                    <Season />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tournaments"
                element={
                  <ProtectedRoute>
                    <Tournaments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tournaments/:id"
                element={
                  <ProtectedRoute>
                    <TournamentDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="/replay/:id" element={<Replay />} />
              <Route path="/endless/replay/:shareId" element={<EndlessReplay />} />
              <Route
                path="/sector/:id"
                element={
                  <ProtectedRoute>
                    <Sector />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/achievements"
                element={
                  <ProtectedRoute>
                    <Achievements />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/builder"
                element={
                  <ProtectedRoute>
                    <Builder />
                  </ProtectedRoute>
                }
              />
              <Route path="/community" element={<Community />} />
              <Route path="/community/:code" element={<CommunityLevel />} />
              <Route path="/u/:id" element={<Creator />} />
              <Route path="/bots/:id" element={<BotDetail />} />
              <Route path="/wanted" element={<Wanted />} />
              <Route path="/founders" element={<Founders />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route
                path="/unlock"
                element={
                  <ProtectedRoute>
                    <Unlock />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/unlock/success"
                element={
                  <ProtectedRoute>
                    <UnlockSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Admin />
                  </ProtectedRoute>
                }
              />
            </Routes>
            </GoogleAuthGate>
            {/* Dynamic-island-style live notification pill. Polls
                /api/notifications every 30s; only renders the first
                time we see a new unread notification (no replay of
                backlog). Hidden inside <BrowserRouter> so it has
                access to useNavigate + useLocation. */}
            <LiveNotificationToast />
          </BrowserRouter>
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "rgba(5,5,10,0.92)",
                border: "1px solid rgba(0,255,255,0.35)",
                color: "#ffffff",
                backdropFilter: "blur(10px)",
              },
            }}
          />
          <IosInstallHint />
          <GlobalErrorHandler />
        </AuthProvider>
      </SoundProvider>
      </AppErrorBoundary>
    </div>
  );
}
